import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, TextField, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Paper, IconButton } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { Edit, Save, Home, ChevronRight, Add, Close, ChatBubbleOutline, ExpandMore, ExpandLess, CheckCircleOutline, RadioButtonUnchecked } from '@mui/icons-material';
import { useProjectStore } from '../store/useProjectStore';
import { Task, TaskStatus, Priority, Role, Subtask } from '../types';
import { formatDate, getPriorityColor, getStatusInfo, getRoleColor } from '../utils/helpers';
import DiscussionModal from '../components/DiscussionModal';
import EditSubtaskModal from '../components/EditSubtaskModal';

interface TaskDetailPageProps {
  edit?: boolean;
}

const TaskDetailPage: React.FC<TaskDetailPageProps> = ({ edit = false }) => {
  const { taskId } = useParams<{ taskId: string }>();
  const { 
    getTaskById, 
    updateTask, 
    addRoleToTask, 
    removeRoleFromTask, 
    addSubtask, 
    deleteSubtask, 
    updateSubtaskStatus,
    addSubSubtask,
    toggleSubSubtask
  } = useProjectStore();
  
  const [task, setTask] = useState<Task | null>(null);
  const [isEditing, setIsEditing] = useState(edit);
  const [editedTask, setEditedTask] = useState<Partial<Task>>({});
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [addSubtaskForms, setAddSubtaskForms] = useState<Record<string, boolean>>({});
  const [newSubtaskData, setNewSubtaskData] = useState<Record<string, Partial<Subtask>>>({});
  const [expandedSubtasks, setExpandedSubtasks] = useState<Record<string, boolean>>({});
  const [addSubSubtaskForms, setAddSubSubtaskForms] = useState<Record<string, boolean>>({});
  const [newSubSubtaskText, setNewSubSubtaskText] = useState<Record<string, string>>({});
  const [discussionModal, setDiscussionModal] = useState<{
    open: boolean;
    taskId: string;
    taskTitle: string;
    subtaskId?: string;
    subtaskTitle?: string;
  }>({ open: false, taskId: '', taskTitle: '' });
  const [editSubtaskModal, setEditSubtaskModal] = useState<{
    open: boolean;
    subtask: Subtask | null;
  }>({ open: false, subtask: null });

  // Helper functions to extract status color and icon
  const getStatusColor = (status: TaskStatus) => {
    return getStatusInfo(status).color;
  };

  const getStatusIcon = (status: TaskStatus) => {
    return getStatusInfo(status).icon;
  };

  const openDiscussion = (subtaskId?: string, subtaskTitle?: string) => {
    if (!task) return;
    setDiscussionModal({
      open: true,
      taskId: task.id,
      taskTitle: task.title,
      subtaskId,
      subtaskTitle
    });
  };

  const closeDiscussion = () => {
    setDiscussionModal({ open: false, taskId: '', taskTitle: '' });
  };

  const handleEditSubtask = (subtask: Subtask) => {
    setEditSubtaskModal({ open: true, subtask });
  };

  const handleCloseEditModal = () => {
    setEditSubtaskModal({ open: false, subtask: null });
    // Refresh task data
    if (taskId) {
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  // Get all unique assignees for the autocomplete
  const allAssignees = task ? Array.from(new Set(
    task.subtasks.flatMap(st => st.assignees || [])
  )) : [];

  useEffect(() => {
    if (taskId) {
      const foundTask = getTaskById(taskId);
      if (foundTask) {
        setTask(foundTask);
        setEditedTask(foundTask);
      }
    }
  }, [taskId, getTaskById]);

  if (!task) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Container>
          <Typography variant="h5" sx={{ mt: 4 }}>
            找不到任務
          </Typography>
        </Container>
      </Box>
    );
  }

  const handleSave = () => {
    if (taskId) {
      updateTask(taskId, editedTask);
      const updatedTask = getTaskById(taskId);
      if (updatedTask) {
        setTask(updatedTask);
        setEditedTask(updatedTask);
      }
      setIsEditing(false);
    }
  };

  const handleAddRole = () => {
    if (newRoleName && taskId) {
      addRoleToTask(taskId, newRoleName as Role);
      setTask({ ...task, roles: [...task.roles, newRoleName as Role] });
      setNewRoleName('');
      setAddRoleDialogOpen(false);
    }
  };

  const handleRemoveRole = (role: Role) => {
    if (taskId && window.confirm(`確定要移除「${role}」職能嗎？相關的子任務也會被移除。`)) {
      removeRoleFromTask(taskId, role);
      setTask({ ...task, roles: task.roles.filter(r => r !== role) });
    }
  };

  const handleAddSubtask = (role: Role) => {
    const subtaskData = newSubtaskData[role];
    if (subtaskData && subtaskData.text && taskId) {
      const newSubtask: Omit<Subtask, 'id'> = {
        text: subtaskData.text,
        role,
        status: 'pending',
        assignees: subtaskData.assignees || [],
        startDate: subtaskData.startDate ? new Date(subtaskData.startDate) : undefined,
        expectedDate: subtaskData.expectedDate ? new Date(subtaskData.expectedDate) : undefined,
        discussionCount: 0,
        subSubtasks: [],
        timeLogs: [],
        reviewHistory: [],
      };
      
      addSubtask(taskId, newSubtask);
      setAddSubtaskForms({ ...addSubtaskForms, [role]: false });
      setNewSubtaskData({ ...newSubtaskData, [role]: {} });
      
      // Refresh task
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  const handleUpdateSubtaskStatus = (subtaskId: string, status: TaskStatus) => {
    if (taskId) {
      updateSubtaskStatus(taskId, subtaskId, status);
      // Refresh task
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  const handleDeleteSubtask = (subtaskId: string) => {
    if (taskId && window.confirm('確定要刪除此子任務嗎？')) {
      deleteSubtask(taskId, subtaskId);
      // Refresh task
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  const getSubtasksByRole = (role: Role) => {
    return task.subtasks.filter(st => st.role === role);
  };

  const getRoleSubtaskCount = (role: Role) => {
    return getSubtasksByRole(role).length;
  };

  const handleToggleSubSubtask = (subtaskId: string, subSubtaskId: string) => {
    if (taskId) {
      toggleSubSubtask(taskId, subtaskId, subSubtaskId);
      // Refresh task
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  const handleAddSubSubtask = (subtaskId: string) => {
    const text = newSubSubtaskText[subtaskId];
    if (text && taskId) {
      addSubSubtask(taskId, subtaskId, text);
      setNewSubSubtaskText({ ...newSubSubtaskText, [subtaskId]: '' });
      setAddSubSubtaskForms({ ...addSubSubtaskForms, [subtaskId]: false });
      // Refresh task
      const updatedTask = getTaskById(taskId);
      if (updatedTask) setTask(updatedTask);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        py: 2.5
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                首頁
              </Link>
              <ChevronRight sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.6)' }} />
              <Link to="/" style={{ color: 'rgba(255, 255, 255, 0.8)', textDecoration: 'none' }}>
                美容師平台
              </Link>
              <ChevronRight sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.6)' }} />
              <span>{task.title}</span>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
                }}
              >
                儲存變更
              </Button>
              <Button
                component={Link}
                to="/"
                variant="contained"
                startIcon={<Home />}
                sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
                }}
              >
                返回首頁
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Task Header Section */}
        <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {isEditing ? (
                  <TextField
                    value={editedTask.title || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '2rem' }}
                  />
                ) : (
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    {task.title}
                  </Typography>
                )}
                <IconButton onClick={() => setIsEditing(!isEditing)} color="primary">
                  <Edit />
                </IconButton>
              </Box>
              
              {/* Priority Selector */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">優先級:</Typography>
                {['high', 'medium', 'low'].map((priority) => (
                  <Chip
                    key={priority}
                    label={priority === 'high' ? '高優先' : priority === 'medium' ? '中優先' : '低優先'}
                    onClick={() => isEditing && setEditedTask({ ...editedTask, priority: priority as Priority })}
                    sx={{
                      bgcolor: getPriorityColor(priority as Priority),
                      color: 'white',
                      border: editedTask.priority === priority ? '3px solid rgba(66, 153, 225, 0.4)' : 'none',
                      cursor: isEditing ? 'pointer' : 'default'
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Task Info Grid */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, borderLeft: '4px solid #667eea' }}>
                <Typography variant="caption" color="text.secondary">專案經理</Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editedTask.pmName || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, pmName: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight={600}>{task.pmName}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, borderLeft: '4px solid #667eea' }}>
                <Typography variant="caption" color="text.secondary">開始時間</Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    value={editedTask.startDate ? new Date(editedTask.startDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditedTask({ ...editedTask, startDate: new Date(e.target.value) })}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight={600}>{formatDate(task.startDate)}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, borderLeft: '4px solid #667eea' }}>
                <Typography variant="caption" color="text.secondary">預計完成時間</Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    type="date"
                    size="small"
                    value={editedTask.expectedDate ? new Date(editedTask.expectedDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditedTask({ ...editedTask, expectedDate: new Date(e.target.value) })}
                    sx={{ mt: 1 }}
                  />
                ) : (
                  <Typography variant="body1" fontWeight={600}>{formatDate(task.expectedDate)}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' } }}>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, borderLeft: '4px solid #667eea' }}>
                <Typography variant="caption" color="text.secondary">實際完成時間</Typography>
                <Typography variant="body1" fontWeight={600} color={task.completedDate ? 'success.main' : 'text.secondary'}>
                  {task.completedDate ? formatDate(task.completedDate) : '進行中'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ bgcolor: 'grey.50', p: 2.5, borderRadius: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>任務描述</Typography>
              {!isEditing && (
                <Button size="small" onClick={() => setIsEditing(true)}>編輯</Button>
              )}
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={editedTask.description || ''}
                onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {task.description}
              </Typography>
            )}
          </Box>

          {/* Progress */}
          <Box>
            <LinearProgress 
              variant="determinate" 
              value={task.progress} 
              sx={{ height: 10, borderRadius: 5 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">完成進度</Typography>
              <Typography variant="body2" fontWeight={600}>{task.progress}%</Typography>
            </Box>
          </Box>
          
          {/* Discussion Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ChatBubbleOutline />}
              onClick={() => openDiscussion()}
            >
              任務討論 ({task.discussionCount})
            </Button>
          </Box>
        </Paper>

        {/* Roles Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
              職能分配
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddRoleDialogOpen(true)}
              size="small"
            >
              新增職能
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {task.roles.map((role) => (
              <Box
                key={role}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'grey.100',
                  px: 2.5,
                  py: 1.5,
                  borderRadius: 25,
                  border: '2px solid transparent',
                  '&:hover': { borderColor: 'grey.300' }
                }}
              >
                <Typography fontWeight={600}>{role}</Typography>
                <Chip label={getRoleSubtaskCount(role)} size="small" color="primary" />
                <IconButton 
                  size="small" 
                  onClick={() => handleRemoveRole(role)}
                  sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Subtasks Section */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
              子任務管理
            </Typography>
            
            <Box sx={{ bgcolor: 'info.lighter', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>注意：</strong>子任務的完成狀態僅能由被分配的負責人員進行變更。
              </Typography>
            </Box>
          </Box>

          {/* Subtask Groups by Role */}
          {task.roles.map((role) => {
            const roleSubtasks = getSubtasksByRole(role);
            const showAddForm = addSubtaskForms[role] || false;
            
            return (
              <Box key={role} sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip 
                      label={role} 
                      sx={{ bgcolor: getRoleColor(role), color: 'white', fontWeight: 600 }}
                    />
                    <Typography variant="h6">{role}</Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setAddSubtaskForms({ ...addSubtaskForms, [role]: true })}
                  >
                    新增子任務
                  </Button>
                </Box>

                {/* Subtask List */}
                {roleSubtasks.length === 0 && !showAddForm && (
                  <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    <Typography variant="h1" sx={{ fontSize: '3rem', mb: 1 }}>📋</Typography>
                    <Typography>尚無子任務，點擊上方按鈕新增</Typography>
                  </Box>
                )}

                {roleSubtasks.map((subtask) => (
                  <Box key={subtask.id} sx={{ bgcolor: 'white', borderRadius: 2, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: getStatusColor(subtask.status),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: 14,
                          flexShrink: 0,
                          mt: 0.5
                        }}
                      >
                        {getStatusIcon(subtask.status)}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body1" 
                          fontWeight={500}
                          sx={{ 
                            textDecoration: subtask.status === 'completed' ? 'line-through' : 'none',
                            color: subtask.status === 'completed' ? 'text.secondary' : 'text.primary',
                            cursor: 'pointer',
                            '&:hover': {
                              color: subtask.status === 'completed' ? 'text.secondary' : 'primary.main',
                              textDecoration: 'underline',
                            }
                          }}
                          onClick={() => handleEditSubtask(subtask)}
                        >
                          {subtask.text}
                        </Typography>
                        
                        {/* Assignees */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {subtask.assignees.length > 0 ? (
                            subtask.assignees.map((assignee) => (
                              <Chip
                                key={assignee}
                                label={assignee}
                                size="small"
                                avatar={
                                  <Box sx={{ 
                                    width: 20, 
                                    height: 20, 
                                    borderRadius: '50%', 
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.65rem',
                                    fontWeight: 600
                                  }}>
                                    {assignee.charAt(0)}
                                  </Box>
                                }
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">尚未指派</Typography>
                          )}
                        </Box>

                        {/* Time Info */}
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: '120px' }}>
                            <Typography variant="caption" color="text.secondary" display="block">開始時間</Typography>
                            <Typography variant="body2">{subtask.startDate ? formatDate(subtask.startDate) : '待開始'}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: '120px' }}>
                            <Typography variant="caption" color="text.secondary" display="block">預計完成</Typography>
                            <Typography variant="body2">{subtask.expectedDate ? formatDate(subtask.expectedDate) : '-'}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: '120px' }}>
                            <Typography variant="caption" color="text.secondary" display="block">實際完成</Typography>
                            <Typography 
                              variant="caption" 
                              color={subtask.status === 'completed' ? 'success.main' : 'text.secondary'}
                            >
                              {subtask.status === 'completed' ? '已完成' : 
                               subtask.status === 'reviewing' ? '驗收中' :
                               subtask.status === 'pending-review' ? '待驗收' :
                               subtask.status === 'in-progress' ? '執行中' : '待執行'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Progress bar for sub-subtasks */}
                        {subtask.subSubtasks && subtask.subSubtasks.length > 1 && (
                          <Box sx={{ mt: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(subtask.subSubtasks.filter(s => s.completed).length / subtask.subSubtasks.length) * 100}
                                sx={{
                                  flex: 1,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: 'action.hover',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: subtask.subSubtasks.every(s => s.completed) ? 'success.main' : 'primary.main',
                                  },
                                }}
                              />
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45, fontWeight: 600 }}>
                                {Math.round((subtask.subSubtasks.filter(s => s.completed).length / subtask.subSubtasks.length) * 100)}%
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Status Selector */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          {(['pending', 'in-progress', 'pending-review', 'reviewing', 'completed'] as TaskStatus[]).map((status) => (
                            <Button
                              key={status}
                              size="small"
                              variant={subtask.status === status ? 'contained' : 'outlined'}
                              onClick={() => handleUpdateSubtaskStatus(subtask.id, status)}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {status === 'pending' ? '待執行' :
                               status === 'in-progress' ? '執行中' :
                               status === 'pending-review' ? '待驗收' :
                               status === 'reviewing' ? '驗收中' : '已完成'}
                            </Button>
                          ))}
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                          <Button size="small" variant="outlined">編輯</Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            onClick={() => handleDeleteSubtask(subtask.id)}
                          >
                            刪除
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => setAddSubSubtaskForms({ ...addSubSubtaskForms, [subtask.id]: true })}
                          >
                            新增細項
                            {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                              <Chip
                                label={`${subtask.subSubtasks.filter(s => s.completed).length}/${subtask.subSubtasks.length}`}
                                size="small"
                                sx={{ ml: 1, height: 20 }}
                              />
                            )}
                          </Button>
                          {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                            <IconButton 
                              size="small"
                              onClick={() => setExpandedSubtasks({ ...expandedSubtasks, [subtask.id]: !expandedSubtasks[subtask.id] })}
                            >
                              {expandedSubtasks[subtask.id] ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          )}
                          <Chip
                            icon={<ChatBubbleOutline />}
                            label={`${subtask.discussionCount}則討論`}
                            size="small"
                            clickable
                            onClick={() => openDiscussion(subtask.id, subtask.text)}
                            sx={{ ml: 'auto' }}
                          />
                        </Box>

                        {/* Sub-subtasks Section */}
                        {(expandedSubtasks[subtask.id] || addSubSubtaskForms[subtask.id]) && (
                          <Box sx={{ mt: 2, ml: 4, borderLeft: '2px dashed #e2e8f0', pl: 2 }}>
                            {/* Existing sub-subtasks */}
                            {subtask.subSubtasks?.map((subSubtask) => (
                              <Box key={subSubtask.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleToggleSubSubtask(subtask.id, subSubtask.id)}
                                  sx={{ p: 0.5 }}
                                >
                                  {subSubtask.completed ? (
                                    <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />
                                  ) : (
                                    <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  )}
                                </IconButton>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    textDecoration: subSubtask.completed ? 'line-through' : 'none',
                                    color: subSubtask.completed ? 'text.secondary' : 'text.primary'
                                  }}
                                >
                                  {subSubtask.text}
                                </Typography>
                              </Box>
                            ))}

                            {/* Add sub-subtask form */}
                            {addSubSubtaskForms[subtask.id] && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField
                                  size="small"
                                  placeholder="輸入細項描述..."
                                  value={newSubSubtaskText[subtask.id] || ''}
                                  onChange={(e) => setNewSubSubtaskText({ ...newSubSubtaskText, [subtask.id]: e.target.value })}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleAddSubSubtask(subtask.id);
                                    }
                                  }}
                                  sx={{ flex: 1 }}
                                />
                                <Button 
                                  size="small" 
                                  variant="contained"
                                  onClick={() => handleAddSubSubtask(subtask.id)}
                                >
                                  新增
                                </Button>
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  onClick={() => {
                                    setAddSubSubtaskForms({ ...addSubSubtaskForms, [subtask.id]: false });
                                    setNewSubSubtaskText({ ...newSubSubtaskText, [subtask.id]: '' });
                                  }}
                                >
                                  取消
                                </Button>
                              </Box>
                            )}

                            {/* Progress indicator */}
                            {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  完成 {subtask.subSubtasks.filter(s => s.completed).length}/{subtask.subSubtasks.length} 個細項
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}

                {/* Add Subtask Form */}
                {showAddForm && (
                  <Box sx={{ bgcolor: 'grey.100', border: '2px dashed #cbd5e0', borderRadius: 2, p: 3, mt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <TextField
                          fullWidth
                          label="子任務名稱"
                          placeholder="輸入子任務名稱"
                          value={newSubtaskData[role]?.text || ''}
                          onChange={(e) => setNewSubtaskData({
                            ...newSubtaskData,
                            [role]: { ...newSubtaskData[role], text: e.target.value }
                          })}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="負責人員"
                          placeholder="輸入負責人員姓名"
                          value={newSubtaskData[role]?.assignees?.[0] || ''}
                          onChange={(e) => setNewSubtaskData({
                            ...newSubtaskData,
                            [role]: { ...newSubtaskData[role], assignees: e.target.value ? [e.target.value] : [] }
                          })}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: '200px' }}>
                          <TextField
                            fullWidth
                            type="date"
                            label="開始時間"
                            InputLabelProps={{ shrink: true }}
                            value={newSubtaskData[role]?.startDate ? new Date(newSubtaskData[role].startDate!).toISOString().split('T')[0] : ''}
                            onChange={(e) => setNewSubtaskData({
                              ...newSubtaskData,
                              [role]: { ...newSubtaskData[role], startDate: new Date(e.target.value) }
                            })}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: '200px' }}>
                          <TextField
                            fullWidth
                            type="date"
                            label="預計完成時間"
                            InputLabelProps={{ shrink: true }}
                            value={newSubtaskData[role]?.expectedDate ? new Date(newSubtaskData[role].expectedDate!).toISOString().split('T')[0] : ''}
                            onChange={(e) => setNewSubtaskData({
                              ...newSubtaskData,
                              [role]: { ...newSubtaskData[role], expectedDate: new Date(e.target.value) }
                            })}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            variant="contained" 
                            onClick={() => handleAddSubtask(role)}
                          >
                            新增
                          </Button>
                          <Button 
                            variant="outlined"
                            onClick={() => {
                              setAddSubtaskForms({ ...addSubtaskForms, [role]: false });
                              setNewSubtaskData({ ...newSubtaskData, [role]: {} });
                            }}
                          >
                            取消
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      </Container>

      {/* Add Role Dialog */}
      <Dialog open={addRoleDialogOpen} onClose={() => setAddRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>新增職能</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="職能名稱"
            placeholder="例如：資料庫管理員、測試工程師"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRoleDialogOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleAddRole}>新增</Button>
        </DialogActions>
      </Dialog>

      {/* Discussion Modal */}
      <DiscussionModal
        open={discussionModal.open}
        onClose={closeDiscussion}
        taskId={discussionModal.taskId}
        taskTitle={discussionModal.taskTitle}
        subtaskId={discussionModal.subtaskId}
        subtaskTitle={discussionModal.subtaskTitle}
      />

      {/* Edit Subtask Modal */}
      {editSubtaskModal.open && task && editSubtaskModal.subtask && (
        <EditSubtaskModal
          open={editSubtaskModal.open}
          onClose={handleCloseEditModal}
          task={task}
          subtask={editSubtaskModal.subtask}
          allAssignees={allAssignees}
        />
      )}
    </Box>
  );
};

export default TaskDetailPage; 